import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardController from '../Controllers/DashboardController';
import MainCompanyController from '../Controllers/MainCompanyController';
import CompanySelect from '../Components/CompanySelect';

const PAGE_SIZE = 25;

export function VerificationLog() {
  const location = useLocation();
  const { auth } = useAuth();
  const isAdmin = ['Admin', 'SuperAdmin'].includes(auth?.role);
  const passedState = location.state || {};

  const [filters, setFilters] = useState({
    action: passedState.action || '',
    status: '',
    from: passedState.from || '',
    to: passedState.to || '',
  });
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({
    data: passedState.initialData || [],
    total: passedState.initialTotal ?? (passedState.initialData || []).length,
  });
  const [loading, setLoading] = useState(!passedState.initialData);
  const [error, setError] = useState('');

  const [mainCompanies, setMainCompanies] = useState([]);
  const [selectedMainCompanyId, setSelectedMainCompanyId] = useState('');

  useEffect(() => {
    if (!isAdmin) return;
    MainCompanyController.list()
      .then((list) => setMainCompanies(list || []))
      .catch((err) => console.error('Failed to load main companies', err));
  }, [isAdmin]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (filters.action) params.action = filters.action;
      if (filters.status) params.status = filters.status;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (selectedMainCompanyId) {
        params.main_company_id = selectedMainCompanyId;
      } else if (passedState.companyId) {
        params.company_id = passedState.companyId;
      }

      const data = await DashboardController.getVerificationLogs(params);
      setResult(data);
    } catch (err) {
      setError('Failed to load verification logs. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, filters.action, filters.status, filters.from, filters.to, selectedMainCompanyId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (field) => (e) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleMainCompanyChange = (id) => {
    setSelectedMainCompanyId(id);
    setPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  const totalPages = Math.max(1, Math.ceil((result.total || 0) / PAGE_SIZE));

  return (
    <div className="main-panel">
      <div className="content-wrapper ivs-page">
        <div className="d-flex align-items-start justify-content-between mb-4 flex-wrap gap-2">
          <div className="text-left">
            <h2 className="mb-1 font-weight-bold text-left page-heading">Verification Logs</h2>
            <p className="text-muted m-0 text-left" style={{ fontSize: 15 }}>
              OTP and login verification events
            </p>
          </div>
        </div>

        <form className="ivs-search-panel" onSubmit={handleSearch}>
          <div className="ivs-search-row" style={{ gridTemplateColumns: isAdmin ? '1fr 1.2fr 1fr 1fr 1fr auto' : '1.2fr 1fr 1fr 1fr auto' }}>
            {isAdmin && (
              <div>
                <label className="ivs-field-label">Main Company</label>
                <CompanySelect
                  companies={mainCompanies}
                  value={selectedMainCompanyId}
                  onChange={handleMainCompanyChange}
                  placeholder="All main companies"
                  variant="field"
                />
              </div>
            )}

            <div>
              <label className="ivs-field-label">Action</label>
              <input
                className="ivs-field"
                placeholder="e.g. OTP_REQUESTED"
                value={filters.action}
                onChange={handleFilterChange('action')}
              />
            </div>

            <div>
              <label className="ivs-field-label">Status</label>
              <select className="ivs-field" value={filters.status} onChange={handleFilterChange('status')}>
                <option value="">All Status</option>
                <option value="SUCCESS">SUCCESS</option>
                <option value="FAILED">FAILED</option>
              </select>
            </div>

            <div>
              <label className="ivs-field-label">From</label>
              <input type="date" className="ivs-field" value={filters.from} onChange={handleFilterChange('from')} />
            </div>

            <div>
              <label className="ivs-field-label">To</label>
              <input type="date" className="ivs-field" value={filters.to} onChange={handleFilterChange('to')} />
            </div>

            <button className="ivs-btn-primary" type="submit" disabled={loading}>
              {loading ? 'Loading…' : 'Search'}
            </button>
          </div>
        </form>

        {error && (
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="bi bi-exclamation-triangle mr-2"></i>
            {error}
          </div>
        )}

        <div className="ivs-panel">
          <table className="table table-striped mb-0">
            <thead>
              <tr>
                <th>Time</th>
                <th>Email / Mobile</th>
                <th>Action</th>
                <th>Status</th>
                <th>Company</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {result.data.length > 0 ? (
                result.data.map((log) => (
                  <tr key={log.id}>
                    <td>{new Date(log.createdAt).toLocaleString()}</td>
                    <td>{log.email || log.mobile || '—'}</td>
                    <td>{log.action}</td>
                    <td>{log.status}</td>
                    <td>{log.company || '—'}</td>
                    <td>{log.ipAddress || '—'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    {loading ? 'Loading…' : 'No logs found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {result.total > 0 && (
            <div className="ivs-pagination">
              <button
                className="ivs-pagination__btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="ivs-pagination__label">
                Page {page} of {totalPages} · {result.total} events
              </span>
              <button
                className="ivs-pagination__btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VerificationLog;
